import { m } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Mail, ArrowDown } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import heroMockupFoods from "@/assets/hero-mockup-foods.webp";
import heroMockupFoods400 from "@/assets/hero-mockup-foods-400.webp";
import heroMockupFoods480 from "@/assets/hero-mockup-foods-480.webp";
import heroMockupFoods576 from "@/assets/hero-mockup-foods-576.webp";
import heroMockupFoods640 from "@/assets/hero-mockup-foods-640.webp";
import heroMockupFoods768 from "@/assets/hero-mockup-foods-768.webp";
import heroMockupFoods800 from "@/assets/hero-mockup-foods-800.webp";
import heroMockupFoods1024 from "@/assets/hero-mockup-foods-1024.webp";
const HeroSection = () => {
  const { t } = useLanguage();

  return (
    <section className="relative min-h-screen bg-hero-gradient overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-fresh-green/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-warm-orange/10 rounded-full blur-3xl" />
      </div>

      <div className="container relative z-10 pt-32 pb-20 lg:pt-40 lg:pb-32">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Text Content */}
          <div className="text-center lg:text-left">
            <span
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-fresh-green-light text-fresh-green text-sm font-medium mb-6"
            >
              <span className="w-2 h-2 rounded-full bg-fresh-green animate-pulse" />
              {t("hero.badge")}
            </span>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-tight mb-6">
              {t("hero.title1")}{" "}
              <span className="text-gradient">{t("hero.title2")}</span>{" "}
              {t("hero.title3")}
            </h1>

            <p className="text-lg sm:text-xl text-muted-foreground mb-8 max-w-xl mx-auto lg:mx-0">
              {t("hero.subtitle")}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-10">
              <Button
                variant="hero"
                size="lg"
                className="group"
                asChild
              >
                <a href="#download">
                  <Mail className="w-5 h-5 transition-transform group-hover:-translate-y-0.5" />
                  {t("hero.cta")}
                </a>
              </Button>
            </div>
          </div>

          {/* Hero Image */}
          <div className="relative flex justify-center lg:justify-end">
            <img
              src={heroMockupFoods576}
              srcSet={`${heroMockupFoods400} 400w, ${heroMockupFoods480} 480w, ${heroMockupFoods576} 576w, ${heroMockupFoods640} 640w, ${heroMockupFoods768} 768w, ${heroMockupFoods800} 800w, ${heroMockupFoods1024} 1024w, ${heroMockupFoods} 1280w`}
              sizes="(max-width: 640px) calc(100vw - 3rem), (max-width: 1024px) 512px, 576px"
              alt={t("hero.imgAlt")}
              className="w-full max-w-lg lg:max-w-xl animate-float h-auto"
              loading="eager"
              fetchPriority="high"
              decoding="async"
              width="576"
              height="329"
            />
          </div>
        </div>

        {/* Scroll indicator */}
        <m.a
          href="#features"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.5 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden lg:flex flex-col items-center gap-2 cursor-pointer hover:text-primary transition-colors"
        >
          <span className="text-sm text-muted-foreground">{t("hero.scrollMore")}</span>
          <m.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
          >
            <ArrowDown className="w-5 h-5 text-muted-foreground" />
          </m.div>
        </m.a>
      </div>
    </section>
  );
};

export default HeroSection;
